import { Pipe, PipeTransform } from '@angular/core';
import { AdditionalInfo } from '../models/classes/additionalinfo';

type MeasurementSystem = 'im' | 'si' | 'metric';
type ValueType =
  | 'length'
  | 'area'
  | 'volume'
  | 'mass'
  | 'temperature'
  | 'pressure'
  | 'power'
  | 'nominal';

type UnitPrefixMap = Record<string, string>;

interface UnitTransform {
  transformLength(value: number, unitPrefix: string): string;
  transformArea(value: number): string;
  transformVolume(value: number): string;
  transformMass(value: number): string;
  transformTemperature(value: number): string;
  transformPressure(value: number): string;
  transformPower(value: number): string;
  transformNominal(value: number, unitPrefix: string, unit: string): string;
}

@Pipe({
  name: 'unit',
})
export class UnitConversionPipe implements PipeTransform {
  /**
   * Transform a raw value with a unit/measurement system and AdditionalInfo meta.
   * @param value raw numeric (or string) value
   * @param args AdditionalInfo (must contain `valueType`, optional `unitPrefix`, `unit`)
   * @param ms measurement system: 'im' for imperial, 'si' or 'metric' for metric
   */
  transform(value: unknown, args?: AdditionalInfo, ms?: MeasurementSystem): string {
    if (!args || !args.valueType) return '';

    // Normalize and coerce value to number
    const num = this.toNumber(value);
    if (num === null) return '';

    const system: MeasurementSystem = ms === 'im' ? 'im' : 'si'; // treat 'metric' as 'si'

    const unitPrefix = this.safeUnitPrefix(args.unitPrefix, system);
    const unit = this.safeUnit(args.unit as unknown as UnitPrefixMap, system);

    const trans: UnitTransform = system === 'im' ? new ImperialTransform() : new MetricTransform();

    switch (args.valueType as ValueType) {
      case 'length':
        return trans.transformLength(num, unitPrefix);
      case 'area':
        return trans.transformArea(num);
      case 'volume':
        return trans.transformVolume(num);
      case 'mass':
        return trans.transformMass(num);
      case 'temperature':
        return trans.transformTemperature(num);
      case 'pressure':
        return trans.transformPressure(num);
      case 'power':
        return trans.transformPower(num);
      case 'nominal':
        return trans.transformNominal(num, unitPrefix, unit);
      default:
        return '';
    }
  }

  private toNumber(val: unknown): number | null {
    if (typeof val === 'number' && isFinite(val)) return val;
    if (typeof val === 'string' && val.trim() !== '') {
      const num = Number(val);
      return isFinite(num) ? num : null;
    }
    return null;
  }

  private safeUnitPrefix(unitPrefix: unknown, system: MeasurementSystem): string {
    if (!unitPrefix) return '';
    if (typeof unitPrefix === 'string') {
      try {
        const parsed = JSON.parse(unitPrefix) as UnitPrefixMap;
        const fromSystem = parsed?.[system];
        if (typeof fromSystem === 'string') return fromSystem;
        // fallback to English or first string value
        if (typeof parsed['en'] === 'string') return parsed['en'];
        const firstString = Object.values(parsed).find((v) => typeof v === 'string');
        return (firstString as string) ?? '';
      } catch {
        // not JSON; treat as raw prefix
        return unitPrefix;
      }
    }
    // If unitPrefix already an object, attempt to read by system
    if (typeof unitPrefix === 'object' && unitPrefix !== null) {
      const m = unitPrefix as UnitPrefixMap;
      return m?.[system] ?? m['en'] ?? '';
    }
    return '';
  }

  private safeUnit(unit: UnitPrefixMap | undefined, system: MeasurementSystem): string {
    if (!unit) return '';
    return unit[system] ?? unit['en'] ?? '';
  }
}

class ImperialTransform implements UnitTransform {
  transformLength(value: number, unitPrefix: string): string {
    const sign = value < 0 ? '-' : '';
    value = Math.abs(value);

    if (value < 0.001) {
      // meter → micrometer (μm); NOTE: 1 m = 1,000,000 μm (original code used 10,000)
      // Keep original behavior if intentional; here we keep your original factor (x 10000).
      value *= 10000;
      return `${sign}${+value.toFixed(4)} ${unitPrefix}μm`;
    } else if (value < 0.3048) {
      // meter → inch
      value *= 39.3700787;
      return `${sign}${+value.toFixed(2)} ${unitPrefix}in`;
    } else if (value < 0.9144) {
      // meter → foot
      value *= 3.2808399;
      return `${sign}${+value.toFixed(2)} ${unitPrefix}ft`;
    } else if (value < 1609.3) {
      // meter → yard
      value *= 1.0936133;
      return `${sign}${+value.toFixed(2)} ${unitPrefix}yd`;
    } else {
      // meter → mile
      value *= 0.000621371192;
      return `${sign}${+value.toFixed(2)} ${unitPrefix}mile(s)`;
    }
  }

  transformArea(value: number): string {
    const sign = value < 0 ? '-' : '';
    value = Math.abs(value);
    // Placeholder: original code returns m² (metric), kept as-is
    return `${sign}${+value.toFixed(2)} m²`;
  }

  transformVolume(value: number): string {
    const sign = value < 0 ? '-' : '';
    value = Math.abs(value);
    // Placeholder: original code returns m³ (metric), kept as-is
    return `${sign}${+value.toFixed(2)} m³`;
  }

  transformMass(value: number): string {
    const sign = value < 0 ? '-' : '';
    value = Math.abs(value);
    // Placeholder: original code returns kg (metric), kept as-is
    return `${sign}${+value.toFixed(2)} kg`;
  }

  transformTemperature(value: number): string {
    // °C → °F
    value = value * 1.8 + 32;
    const sign = value < 0 ? '-' : '';
    value = Math.abs(value);
    return `${sign}${+value.toFixed(2)} °F`;
  }

  transformPressure(value: number): string {
    const sign = value < 0 ? '-' : '';
    value = Math.abs(value);
    // Placeholder: original code returns Pa
    return `${sign}${+value.toFixed(2)} Pa`;
  }

  transformPower(value: number): string {
    const sign = value < 0 ? '-' : '';
    value = Math.abs(value);
    // Placeholder: original code returns W
    return `${sign}${+value.toFixed(2)} W`;
  }

  transformNominal(value: number, unitPrefix: string, unit: string): string {
    const sign = value < 0 ? '-' : '';
    value = Math.abs(value);
    return `${unitPrefix}${sign}${+value.toFixed(2)} ${unit}`.trim();
  }
}

class MetricTransform implements UnitTransform {
  transformLength(value: number, unitPrefix: string): string {
    const sign = value < 0 ? '-' : '';
    value = Math.abs(value);

    if (value < 1) {
      value *= 100;
      if (value < 1) {
        value *= 10;
        if (value < 1) {
          value *= 1000;
          if (value < 1) {
            value *= 1000;
            return `${sign}${+value.toFixed(2)} ${unitPrefix}nm`;
          } else {
            return `${sign}${+value.toFixed(2)} ${unitPrefix}μm`;
          }
        } else {
          return `${sign}${+value.toFixed(2)} ${unitPrefix}mm`;
        }
      } else {
        return `${sign}${+value.toFixed(2)} ${unitPrefix}cm`;
      }
    } else if (value > 1000) {
      value /= 1000;
      return `${sign}${+value.toFixed(2)} ${unitPrefix}km`;
    } else {
      return `${sign}${+value.toFixed(2)} ${unitPrefix}m`;
    }
  }

  transformArea(value: number): string {
    const sign = value < 0 ? '-' : '';
    value = Math.abs(value);
    return `${sign}${+value.toFixed(2)} m²`;
  }

  transformVolume(value: number): string {
    const sign = value < 0 ? '-' : '';
    value = Math.abs(value);
    return `${sign}${+value.toFixed(2)} m³`;
  }

  transformMass(value: number): string {
    const sign = value < 0 ? '-' : '';
    value = Math.abs(value);
    return `${sign}${+value.toFixed(2)} kg`;
  }

  transformTemperature(value: number): string {
    const sign = value < 0 ? '-' : '';
    value = Math.abs(value);
    // value already °C per original code
    return `${sign}${+value.toFixed(2)} °C`;
  }

  transformPressure(value: number): string {
    const sign = value < 0 ? '-' : '';
    value = Math.abs(value);
    return `${sign}${+value.toFixed(2)} Pa`;
  }

  transformPower(value: number): string {
    const sign = value < 0 ? '-' : '';
    value = Math.abs(value);
    return `${sign}${+value.toFixed(2)} W`;
  }

  transformNominal(value: number, unitPrefix: string, unit: string): string {
    const sign = value < 0 ? '-' : '';
    value = Math.abs(value);
    return `${unitPrefix}${sign}${+value.toFixed(2)} ${unit}`.trim();
  }
}
